import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
	KeyRound,
	UserCircle,
	ArrowRight,
	Loader2,
	ShieldCheck,
} from "lucide-react";

type Step = "account" | "login" | "register";

export default function AuthPage() {
	const { checkStatus, login, register } = useAuth();
	const [step, setStep] = useState<Step>("account");
	const [accountName, setAccountName] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const handleCheckAccount = async () => {
		if (!accountName.trim()) return;
		setLoading(true);
		setError(null);
		try {
			const status = await checkStatus(accountName.trim());
			setStep(status === "initialized" ? "login" : "register");
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "Failed to check account status",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleLogin = async () => {
		if (!password) return;
		setLoading(true);
		setError(null);
		try {
			await login(accountName.trim(), password);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Login failed");
		} finally {
			setLoading(false);
		}
	};

	const handleRegister = async () => {
		if (!password || !confirmPassword) return;
		if (password !== confirmPassword) {
			setError("Passwords do not match");
			return;
		}
		if (password.length < 6) {
			setError("Password must be at least 6 characters");
			return;
		}
		setLoading(true);
		setError(null);
		try {
			await register(accountName.trim(), password);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Registration failed",
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
			<div className="w-full max-w-md">
				{/* Logo */}
				<div className="text-center mb-8">
					<div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 mb-4">
						<ShieldCheck className="w-8 h-8 text-blue-400" />
					</div>
					<h1 className="text-2xl font-bold text-white">
						Cyber Faithful Pal
					</h1>
					<p className="text-slate-400 mt-1 text-sm">
						Your personal knowledge graph assistant
					</p>
				</div>

				{/* Card */}
				<div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 shadow-2xl">
					{step === "account" && (
						<>
							<h2 className="text-lg font-semibold text-white mb-1">
								Sign In
							</h2>
							<p className="text-slate-400 text-sm mb-6">
								Enter your account name to continue
							</p>
							<div className="space-y-4">
								<div className="relative">
									<UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
									<input
										type="text"
										value={accountName}
										onChange={(e) =>
											setAccountName(e.target.value)
										}
										onKeyDown={(e) =>
											e.key === "Enter" &&
											handleCheckAccount()
										}
										placeholder="Account name"
										autoFocus
										className="w-full pl-11 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
									/>
								</div>
								<button
									onClick={handleCheckAccount}
									disabled={loading || !accountName.trim()}
									className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-xl transition-all"
								>
									{loading ? (
										<Loader2 className="w-5 h-5 animate-spin" />
									) : (
										<>
											<span>Continue</span>
											<ArrowRight className="w-4 h-4" />
										</>
									)}
								</button>
							</div>
						</>
					)}

					{step === "login" && (
						<>
							<h2 className="text-lg font-semibold text-white mb-1">
								Welcome back
							</h2>
							<p className="text-slate-400 text-sm mb-6">
								Enter password for{" "}
								<span className="text-blue-400">
									{accountName}
								</span>
							</p>
							<div className="space-y-4">
								<div className="relative">
									<KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
									<input
										type="password"
										value={password}
										onChange={(e) =>
											setPassword(e.target.value)
										}
										onKeyDown={(e) =>
											e.key === "Enter" && handleLogin()
										}
										placeholder="Password"
										autoFocus
										className="w-full pl-11 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
									/>
								</div>
								<button
									onClick={handleLogin}
									disabled={loading || !password}
									className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-xl transition-all"
								>
									{loading ? (
										<Loader2 className="w-5 h-5 animate-spin" />
									) : (
										"Sign In"
									)}
								</button>
								<button
									onClick={() => {
										setStep("account");
										setPassword("");
										setError(null);
									}}
									className="w-full py-2 text-sm text-slate-400 hover:text-white transition-colors"
								>
									Use a different account
								</button>
							</div>
						</>
					)}

					{step === "register" && (
						<>
							<h2 className="text-lg font-semibold text-white mb-1">
								Create Account
							</h2>
							<p className="text-slate-400 text-sm mb-6">
								Set a password for{" "}
								<span className="text-blue-400">
									{accountName}
								</span>
							</p>
							<div className="space-y-4">
								<div className="relative">
									<KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
									<input
										type="password"
										value={password}
										onChange={(e) =>
											setPassword(e.target.value)
										}
										placeholder="Password"
										autoFocus
										className="w-full pl-11 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
									/>
								</div>
								<div className="relative">
									<KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
									<input
										type="password"
										value={confirmPassword}
										onChange={(e) =>
											setConfirmPassword(e.target.value)
										}
										onKeyDown={(e) =>
											e.key === "Enter" &&
											handleRegister()
										}
										placeholder="Confirm password"
										className="w-full pl-11 pr-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
									/>
								</div>
								<button
									onClick={handleRegister}
									disabled={
										loading ||
										!password ||
										!confirmPassword
									}
									className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-xl transition-all"
								>
									{loading ? (
										<Loader2 className="w-5 h-5 animate-spin" />
									) : (
										"Create Account"
									)}
								</button>
								<button
									onClick={() => {
										setStep("account");
										setPassword("");
										setConfirmPassword("");
										setError(null);
									}}
									className="w-full py-2 text-sm text-slate-400 hover:text-white transition-colors"
								>
									Use a different account
								</button>
							</div>
						</>
					)}

					{error && (
						<div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
							{error}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
